import { defineStore } from "pinia";
import IndexedDbService from "@/service/indexedDbService";
import { hasLocalStorage, lsGetJson } from "@/helpers/storage.js";
import { parseSeasonApiResult } from "@/helpers/season.js";
import {
    EMPTY_DELIVERY,
    extractDeliveryMeta,
    hasDeliveryPeriods,
} from "@/helpers/delivery.js";
import deliveryService from "@/service/deliveryService";

/** Odoo may send analytic ids as numbers or strings; strict `includes(areaId)` misses when types differ. */
function categoryMatchesArea(category, areaIdRaw) {
    const ids = category?.analytic_account_ids;
    if (!Array.isArray(ids) || ids.length === 0) return false;
    const want = Number(areaIdRaw);
    if (Number.isNaN(want)) return false;
    return ids.some((x) => Number(x) === want);
}

export const useProductsStore = defineStore({
    id: "products",
    state: () => ({
        categories: [],
        fastDeliveryData: [],
        delivery: { ...EMPTY_DELIVERY },
        cities: [],
        payment_methods: [],
        fast_status_messages: [],
        feedback_questions: [],
        selectedAria: {
            name: "منطقة الرياض",
            id: 1
        },
        selectedCategoryId: 1,
        productDetails: null,
        SelectedCity: {},
        searchQuery: "",
        isLoading: false,
        loadingStage: "idle",
        loadingProgress: 0,
        isFirstVisit: false,
        isFastDelivery: false,
        isSeasonal: false,
        selectedSeasonalTime: null,
        selectedSeasonalDate: null,
        seasonActive: false,
        seasonId: null,
        seasonDays: [],
        /** Client finished `fetchVersionAndUpdateProducts` (success or failure). */
        catalogBootstrapAttempted: false,
        catalogBootstrapError: null,
        dbService: null,
        /**
         * Odoo catalog revision from last IndexedDB hydrate (`_catalogVersionToken` on stored row).
         * Used with `v3/endpoint/version_parameter` when localStorage `VersionParameter` is missing.
         */
        hydratedCatalogVersionToken: null,
        /**
         * SSR-only: product row from `getCachedProductById` (server) so `/product/:id` can render without full catalog.
         * Ignored in `findProductById` when the route id does not match. Cleared after SSR render in entry-server is optional;
         * stale entries are harmless because id must match.
         */
        ssrProductBootstrap: null,
    }),

    getters: {
        allCategories(state) {
            return state.categories;
        },
        fastDelivery(state) {
            return state.fastDeliveryData;
        },
        fastDeliveryProducts(state) {
            // Get all products from fast delivery categories
            let products = [];
            if (state.fastDeliveryData && state.fastDeliveryData.length > 0) {
                state.fastDeliveryData.forEach(category => {
                    if (category.categ_ids && category.categ_ids.length > 0) {
                        products = [...products, ...category.categ_ids];
                    }
                });
            }
            return products;
        },

        selectedCategory(state) {
            return state.categories.find((c) => c.id === state.selectedCategoryId);
        },

        getSettingData() {
            return lsGetJson("result", null);
        },

        categoryProduct() {
            return this.selectedCategory?.categ_ids
                ? this.selectedCategory.categ_ids
                : [];
        },
    },

    actions: {
        setLoadingStage(stage, progress = null) {
            this.loadingStage = stage;
            if (progress != null) {
                this.loadingProgress = Math.max(this.loadingProgress, Number(progress) || 0);
            }
        },
        resetLoadingStage() {
            this.loadingStage = "idle";
            this.loadingProgress = 0;
        },
        markFirstVisitState() {
            if (!hasLocalStorage()) {
                this.isFirstVisit = true;
                return;
            }
            this.isFirstVisit = !localStorage.getItem("catalog_seen_once");
        },
        markCatalogSeenOnce() {
            if (!hasLocalStorage()) {
                return;
            }
            localStorage.setItem("catalog_seen_once", "1");
            this.isFirstVisit = false;
        },
        _perfMark(label, startTs) {
            const elapsed = Date.now() - startTs;
            console.info(`[catalog-perf] ${label} elapsed_ms=${elapsed}`);
        },
        async _getVersionParameterWithTimeout(timeoutMs = 2500) {
            const timeoutPromise = new Promise((resolve) => {
                setTimeout(() => resolve(undefined), timeoutMs);
            });
            return Promise.race([this.getVersionParameter(), timeoutPromise]);
        },
        async _fetchPrebuiltBootstrap() {
            try {
                const response = await fetch("/internal/catalog/bootstrap", {
                    method: "GET",
                    headers: { Accept: "application/json" },
                });
                if (!response.ok) return null;
                const json = await response.json();
                const catalog = json?.catalog;
                const version = json?.version_parameter != null ? String(json.version_parameter) : null;
                return {
                    catalog: catalog && typeof catalog === "object" ? catalog : null,
                    version_parameter: version,
                };
            } catch (error) {
                console.warn("Failed to fetch prebuilt bootstrap.", error);
                return null;
            }
        },
        turnOnLoading() {
            this.isLoading = true;
            this.setLoadingStage("bootstrap", 5);
        },
        turnOffLoading() {
            this.isLoading = false;
            this.setLoadingStage("done", 100);
        },
        setSearchQuery(query) {
            this.searchQuery = query;
        },
        emptySearch() {
            if (typeof document !== "undefined") {
                const el = document.getElementById("searchInput");
                if (el) {
                    el.value = "";
                }
            }
            this.searchQuery = "";
        },
        selectCity(id) {
            const cities = this.getSettingData.cities;
            const selectedCity = cities.find(
                (selectedCity) => selectedCity.id === id
            );
            this.SelectedCity = selectedCity;
        },

        /**
         * Apply catalog rows already stored in IndexedDB (key 0) to Pinia. No network.
         * @returns {boolean} true if data was applied
         */
        _applyCatalogFromIdbRow(data) {
            if (!data) {
                return false;
            }
            const row = data;
            if (row.id !== undefined) {
                delete row.id;
            }
            this.fillData(row._delivery || undefined);
            const fastDeliveryData = Object.values(row).filter(
                (cat) =>
                    categoryMatchesArea(cat, this.selectedAria.id) && cat.is_fastdelivery
            );
            const areaData = Object.values(row).filter(
                (cat) =>
                    categoryMatchesArea(cat, this.selectedAria.id) && !cat.is_fastdelivery
            );
            this.fastDeliveryData = fastDeliveryData;
            this.categories = areaData;
            return true;
        },

        /** Instant UI from IndexedDB while version is checked (client only). */
        async hydrateFromIndexedDbIfAvailable() {
            if (import.meta.env.SSR || typeof indexedDB === "undefined") {
                return false;
            }
            this.hydratedCatalogVersionToken = null;
            let dbVersion = await IndexedDbService.getVersion("tazej");
            if (!dbVersion || isNaN(dbVersion)) {
                dbVersion = 1;
            }
            this.dbService = new IndexedDbService("tazej", dbVersion);
            try {
                const db = await this.dbService.openConnection();
                const data = await db.get("categories", 0);
                if (data && typeof data === "object" && data._catalogVersionToken != null) {
                    this.hydratedCatalogVersionToken = String(data._catalogVersionToken);
                }
                if (data) {
                    this.setLoadingStage("applyData", 40);
                }
                return this._applyCatalogFromIdbRow(data);
            } catch (error) {
                const name = error?.name || "";
                if (name !== "NotFoundError" && name !== "InvalidStateError") {
                    console.error("hydrateFromIndexedDbIfAvailable:", error);
                }
                return false;
            }
        },

        async handleProductData(isCached = true) {
            this.setLoadingStage("catalogFetch", 55);
            // Always get the current version from the database
            let dbVersion = await IndexedDbService.getVersion("tazej");
            if (!dbVersion || isNaN(dbVersion)) dbVersion = 1;

            this.dbService = new IndexedDbService("tazej", dbVersion);

            try {
                const db = await this.dbService.openConnection();

                let data;
                let areaData;
                if (!isCached) {
                    const freshStart = Date.now();
                    data = await this.getProductsData();
                    if (data) {
                        areaData = data.filter((cat) =>
                            categoryMatchesArea(cat, this.selectedAria.id)
                        );
                        areaData.id = 0;
                        const odooCatalogToken = hasLocalStorage()
                            ? localStorage.getItem("VersionParameter")
                            : null;
                        if (odooCatalogToken) {
                            areaData._catalogVersionToken = odooCatalogToken;
                        }
                        if (hasDeliveryPeriods(this.delivery) || this.delivery?.delivery_fees?.length) {
                            areaData._delivery = { ...this.delivery };
                        }
                        await db.put("categories", areaData);
                        this.setLoadingStage("applyData", 75);
                        this._applyCatalogFromIdbRow(areaData);
                        this._perfMark("catalog_fresh_fetch", freshStart);
                    } else {
                        console.warn("No catalog received from prebuilt bootstrap endpoint.");
                    }

          

                } else {
                    data = await db.get("categories", 0);
                    if (data) {
                        this.setLoadingStage("applyData", 75);
                        this._applyCatalogFromIdbRow(data);
                    } else {
                        console.warn("IndexedDB catalog empty; fetching fresh bootstrap.");
                        const freshStart = Date.now();
                        const fresh = await this.getProductsData();
                        if (fresh) {
                            const areaData = fresh.filter((cat) =>
                                categoryMatchesArea(cat, this.selectedAria.id)
                            );
                            areaData.id = 0;
                            const odooCatalogToken = hasLocalStorage()
                                ? localStorage.getItem("VersionParameter")
                                : null;
                            if (odooCatalogToken) {
                                areaData._catalogVersionToken = odooCatalogToken;
                            }
                            if (hasDeliveryPeriods(this.delivery) || this.delivery?.delivery_fees?.length) {
                                areaData._delivery = { ...this.delivery };
                            }
                            await db.put("categories", areaData);
                            this.setLoadingStage("applyData", 75);
                            this._applyCatalogFromIdbRow(areaData);
                            this._perfMark("catalog_idb_miss_fresh_fetch", freshStart);
                        }
                    }
                }

            } catch (error) {
                // If version error, delete DB and reload page
                if (error.name === "VersionError" || error.message?.includes("version")) {
                    if (typeof indexedDB !== "undefined") {
                        indexedDB.deleteDatabase("tazej");
                    }
                    if (typeof window !== "undefined") {
                        window.location.reload();
                    }
                } else {
                    console.error('Failed to open database connection or process data:', error);
                }
            }
        },
        fillData(data) {
            const deliveryFromStorage = lsGetJson("delivery", {}) || {};
            this.delivery = extractDeliveryMeta(data, deliveryFromStorage);
            if (hasLocalStorage() && hasDeliveryPeriods(this.delivery)) {
                localStorage.setItem("delivery", JSON.stringify(this.delivery));
            }
            this.cities = (data?.cities ?? lsGetJson("cities", [])) || [];
            this.payment_methods = (data?.payment_methods ?? lsGetJson("payment_methods", [])) || [];
            this.fast_status_messages = (data?.fast_status_messages ?? lsGetJson("fast_status_messages", [])) || [];
            this.feedback_questions = (data?.feedback_questions ?? lsGetJson("feedback_questions", [])) || [];
            this.selectedAria =
                data?.cities?.filter((city) => city.id === 1)[0] ?? lsGetJson("selectedAria", this.selectedAria);
        },

        /** Load delivery periods if Pinia/localStorage missed them after catalog SSR/IDB hydrate. */
        async ensureDeliveryPeriods() {
            if (hasDeliveryPeriods(this.delivery)) {
                return true;
            }
            const fromLs = lsGetJson("delivery", {});
            if (hasDeliveryPeriods(fromLs)) {
                this.delivery = extractDeliveryMeta(fromLs);
                return true;
            }
            const boot = await this._fetchPrebuiltBootstrap();
            if (boot?.catalog) {
                this.delivery = extractDeliveryMeta(boot.catalog, this.delivery);
                if (hasLocalStorage() && hasDeliveryPeriods(this.delivery)) {
                    localStorage.setItem("delivery", JSON.stringify(this.delivery));
                }
            }
            return hasDeliveryPeriods(this.delivery);
        },

        async fetchSeasonStatus() {
            if (import.meta.env.SSR) {
                return;
            }
            try {
                const response = await deliveryService.getSeason();
                const parsed = parseSeasonApiResult(response?.data?.result);
                this.seasonActive = parsed.inSeason;
                this.seasonId = parsed.seasonId;
                this.seasonDays = parsed.days;
            } catch (error) {
                console.warn("Failed to fetch season status.", error);
            }
        },

        async getProductsData() {
            try {
                this.isLoading = true; // Start loading
                const response = await this._fetchPrebuiltBootstrap();

                if (response?.catalog) {
                    const result = response.catalog;
                    this.fillData(result);

                    if (hasLocalStorage()) {
                        localStorage.setItem("cities", JSON.stringify(result.cities || []));
                        localStorage.setItem("payment_methods", JSON.stringify(result.payment_methods || []));
                        localStorage.setItem("fast_status_messages", JSON.stringify(result.fast_status_messages || []));
                        localStorage.setItem("feedback_questions", JSON.stringify(result.feedback_questions || []));
                        localStorage.setItem(
                            "selectedAria",
                            JSON.stringify((result.cities || []).filter((city) => city.id === 1)[0])
                        );
                        if (response?.version_parameter) {
                            localStorage.setItem("VersionParameter", response.version_parameter);
                        }
                    }

                    await this.fetchSeasonStatus();

                    return result.categories || [];

                } else {
                    console.error("Failed to fetch products data."); // Error handling
                }
            } catch (error) {
                console.error("Error fetching products data:", error); // Error handling
            } finally {
                this.isLoading = false; // End loading
            }

        },

        /**
         * Odoo “catalog cache” revision: lightweight check before downloading full `get_products_data`.
         * Endpoint: `v3/endpoint/version_parameter` → `result.version_parameter` (string/number).
         * When this value changes, the storefront must refresh IndexedDB + Pinia (see `fetchVersionAndUpdateProducts`).
         */
        async getVersionParameter() {
            try {
                const response = await fetch("/internal/catalog/version", {
                    method: "GET",
                    headers: { Accept: "application/json" },
                });
                if (!response.ok) {
                    return undefined;
                }
                const data = await response.json();
                const raw = data?.version_parameter;
                return raw != null ? String(raw) : undefined;
            } catch (error) {
                console.error("Failed to fetch version parameter.", error); // Error handling
            }
        },
        async handleSsrBootstrap() {
            // In SSR request path we never call Odoo directly; SSR server injects bootstrap payload.
            return;
        },

        /**
         * Client catalog pipeline:
         * 1) Hydrate Pinia from IndexedDB immediately (optional).
         * 2) Call Odoo `version_parameter`; compare to `localStorage.VersionParameter` **or** `_catalogVersionToken` on the IDB row.
         * 3) Same value → `handleProductData(true)` (read cache). Different / missing → save token + `handleProductData(false)` (full API + IDB write).
         */
        async _ensureCatalogAfterBootstrap() {
            const hasCatalog =
                (this.categories?.length > 0) || (this.fastDeliveryData?.length > 0);
            if (hasCatalog) {
                this.catalogBootstrapError = null;
                return;
            }
            console.warn("[catalog] Pinia still empty after bootstrap; forcing fresh fetch.");
            await this.handleProductData(false);
            if (!(this.categories?.length > 0) && !(this.fastDeliveryData?.length > 0)) {
                this.catalogBootstrapError = "catalog_empty";
            }
        },

        async fetchVersionAndUpdateProducts() {
            if (import.meta.env.SSR) {
                if ((this.categories?.length) || (this.fastDeliveryData?.length)) {
                    return;
                }
                await this.handleSsrBootstrap();
                return;
            }

            this.catalogBootstrapError = null;
            this.setLoadingStage("bootstrap", 5);
            this.markFirstVisitState();
            await this.hydrateFromIndexedDbIfAvailable();
            const hasCatalog =
                (this.categories?.length > 0) || (this.fastDeliveryData?.length > 0);
            if (!hasCatalog) {
                this.isLoading = true;
            } else {
                this.isLoading = false;
            }

            try {
                this.setLoadingStage("versionCheck", 15);
                const versionStart = Date.now();
                const versionFromAPI = await this._getVersionParameterWithTimeout(2500);
                this._perfMark("catalog_version_check", versionStart);
                const storedVersion = hasLocalStorage() ? localStorage.getItem("VersionParameter") : null;
                const effectiveStored = storedVersion ?? this.hydratedCatalogVersionToken;

                if (versionFromAPI) {
                    if (effectiveStored && effectiveStored === versionFromAPI) {
                        if (hasLocalStorage() && !storedVersion) {
                            localStorage.setItem("VersionParameter", versionFromAPI);
                        }
                        if (!hasCatalog) await this.handleProductData(true);
                    } else {
                        if (hasLocalStorage()) {
                            localStorage.setItem("VersionParameter", versionFromAPI);
                        }
                        await this.handleProductData(false);
                    }
                } else {
                    console.warn("Could not retrieve version from API in time; keeping cached catalog.");
                    if (!hasCatalog) {
                        await this.handleProductData(true);
                    }
                }
                await this._ensureCatalogAfterBootstrap();
                await this.ensureDeliveryPeriods();
                await this.fetchSeasonStatus();
                if ((this.categories?.length) || (this.fastDeliveryData?.length)) {
                    this.markCatalogSeenOnce();
                }
            } catch (error) {
                console.error("Error updating products:", error);
                this.catalogBootstrapError = "bootstrap_failed";
                await this._ensureCatalogAfterBootstrap();
                await this.ensureDeliveryPeriods();
            } finally {
                this.catalogBootstrapAttempted = true;
                this.isLoading = false;
                this.setLoadingStage("done", 100);
            }
        },
        fetchProductDetails(id) {
            const found = this.findProductById(id);
            this.productDetails = found ? found.product : null;
            if (!this.productDetails) {
                console.log("No product found with the given ID.");
            }
        },

        /**
         * Called from entry-server before `renderToString` when `bootstrapProduct` exists (catalog cache).
         * @param {{ product: object, isSeasonal?: boolean, isFastDelivery?: boolean, categoryName?: string, categoryId?: number | null } | null} found
         */
        setSsrProductBootstrap(found) {
            if (found?.product?.id != null) {
                this.ssrProductBootstrap = found;
            }
        },

        clearSsrProductBootstrap() {
            this.ssrProductBootstrap = null;
        },

        /**
         * بحث المنتج في التصنيفات المحمّلة وفي localStorage (لصفحة /product/:id).
         * @returns {{ product: object, isSeasonal: boolean, isFastDelivery: boolean, categoryName: string, categoryId: number } | null}
         */
        findProductById(rawId) {
            const idNum = Number(rawId);
            if (Number.isNaN(idNum)) return null;

            const boot = this.ssrProductBootstrap;
            if (boot?.product != null) {
                const pid = Number(boot.product.id);
                if (!Number.isNaN(pid) && (pid === idNum || String(boot.product.id) === String(rawId))) {
                    return {
                        product: boot.product,
                        isSeasonal: !!boot.isSeasonal,
                        isFastDelivery: !!boot.isFastDelivery,
                        categoryName: boot.categoryName || "",
                        categoryId: boot.categoryId ?? null,
                    };
                }
            }

            const matchInCategory = (category) => {
                if (!category?.categ_ids?.length) return null;
                const p = category.categ_ids.find(
                    (x) => x.id === idNum || String(x.id) === String(rawId)
                );
                return p
                    ? {
                          product: p,
                          isSeasonal: !!category.is_seasonal,
                          isFastDelivery: !!category.is_fastdelivery,
                          categoryName: category.name || "",
                          categoryId: category.id,
                      }
                    : null;
            };

            for (const category of this.categories || []) {
                const hit = matchInCategory(category);
                if (hit) return hit;
            }
            for (const category of this.fastDeliveryData || []) {
                const hit = matchInCategory(category);
                if (hit) {
                    return {
                        product: hit.product,
                        isSeasonal: hit.isSeasonal,
                        isFastDelivery: true,
                        categoryName: category.name || "",
                        categoryId: category.id,
                    };
                }
            }

            if (hasLocalStorage()) {
                try {
                    const raw = localStorage.getItem("categories");
                    const parsed = raw ? JSON.parse(raw) : [];
                    const list = Array.isArray(parsed) ? parsed : Object.values(parsed || {});
                    for (const category of list) {
                        const hit = matchInCategory(category);
                        if (hit) return hit;
                    }
                } catch {
                    /* ignore */
                }
            }

            return null;
        },

        /**
         * بحث التصنيف الرئيسي (غير التوصيل السريع) لصفحة /category/:id.
         * @returns {{ category: object, isFastDelivery: false } | null}
         */
        findCategoryById(rawId) {
            const idNum = Number(rawId);
            if (Number.isNaN(idNum)) return null;

            const match = (category) => {
                if (!category || category.is_fastdelivery) return null;
                if (category.id === idNum || String(category.id) === String(rawId)) {
                    return { category, isFastDelivery: false };
                }
                return null;
            };

            for (const category of this.categories || []) {
                const hit = match(category);
                if (hit) return hit;
            }

            if (hasLocalStorage()) {
                try {
                    const raw = localStorage.getItem("categories");
                    const parsed = raw ? JSON.parse(raw) : [];
                    const list = Array.isArray(parsed) ? parsed : Object.values(parsed || {});
                    for (const category of list) {
                        const hit = match(category);
                        if (hit) return hit;
                    }
                } catch {
                    /* ignore */
                }
            }

            return null;
        },
        async updateImagesWithFullResolution() {
            return;
        },
    },
});
